import React, { useContext, useMemo } from "react";
import { useGetCustomerQuery } from "../../../api/customerApi";
import { CopyAssistantContext } from "../CopyAssistantContext/CopyAssistantContext";
import { LanguageCode } from "../../../customers/customerlanguages";
import { findLanguage, getCustomerEnglishLanguage } from "../utils";
import { Sentence } from "../types";

export const compactRowTestId = (sectionName: string): string =>
  `GeneratedResultCompactRow-${sectionName}`;

export const compactRowSetActiveTestId = (sectionName: string): string =>
  `GeneratedResultCompactRow-${sectionName}-set-active`;

const PREVIEW_MAX_CHARS = 120;

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

type Props = {
  sectionName: string;
  sentences: Sentence[];
  setActiveSection: (sentence: Sentence) => void;
  onOpen: () => void;
};

export const GeneratedResultCompactRow: React.FC<Props> = ({
  sectionName,
  sentences,
  setActiveSection,
  onOpen,
}) => {
  const { data: customer } = useGetCustomerQuery();
  const {
    prompts: { list: promptList },
  } = useContext(CopyAssistantContext);

  // In compact mode we preview only the first sentence; additional versions
  // are reachable through the modal's version pagination.
  const sentence = sentences[0];

  const previewLanguageCode = useMemo<LanguageCode | undefined>(() => {
    if (!customer) return undefined;
    const prompt =
      promptList.find(({ id }) => id === sentence?.prompt_id) ?? null;
    const language =
      findLanguage(prompt, customer) ?? getCustomerEnglishLanguage(customer);
    return language?.code;
  }, [customer, promptList, sentence?.prompt_id]);

  if (!sentence) return null;

  const previewText = previewLanguageCode
    ? sentence.value?.[previewLanguageCode] ?? ""
    : "";
  const versionCount = sentences.length;
  const isActive = Boolean(sentence.sectionInfo?.active);
  const canSetActive = !isActive && Boolean(sentence.sectionInfo?.textBlockId);

  const handleRowClick = (): void => {
    onOpen();
  };

  const handleSetActiveClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    e.stopPropagation();
    setActiveSection(sentence);
  };

  const baseClasses = [
    "tw-group",
    "tw-relative",
    "tw-flex",
    "tw-items-center",
    "tw-gap-3",
    "tw-px-3",
    "tw-py-2",
    "tw-cursor-pointer",
    "tw-border-b",
    "tw-border-b-gray-200",
    "hover:tw-bg-gray-50",
  ];
  const activeClasses = isActive
    ? ["tw-border-l-4", "tw-border-l-primary", "tw-pl-2"]
    : [];
  const rowClassName = [...baseClasses, ...activeClasses].join(" ");

  return (
    <div
      role="button"
      onClick={handleRowClick}
      data-testid={compactRowTestId(sectionName)}
      className={rowClassName}
    >
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
        <span className="tw-font-medium tw-text-sm tw-truncate tw-max-w-[40%]">
          {sectionName}
        </span>

        {previewLanguageCode && (
          <span className="tw-text-xs tw-uppercase tw-text-gray-500 tw-shrink-0">
            {previewLanguageCode}
          </span>
        )}

        {versionCount > 1 && (
          <span className="tw-text-xs tw-text-gray-500 tw-px-1.5 tw-rounded tw-bg-gray-100 tw-shrink-0">
            {versionCount}
          </span>
        )}

        <span className="tw-text-sm tw-text-gray-600 tw-truncate tw-flex-1">
          {truncate(previewText, PREVIEW_MAX_CHARS)}
        </span>
      </div>

      <div className="tw-flex tw-items-center tw-gap-1 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
        {canSetActive && (
          <button
            type="button"
            className="txu-btn txu-icon-only txu-compact txu-no-border"
            onClick={handleSetActiveClick}
            data-testid={compactRowSetActiveTestId(sectionName)}
            title="Set active"
          >
            <i className="material-icons">check_circle</i>
          </button>
        )}
      </div>
    </div>
  );
};
